/**
 * Action-type namespaces of the ducks whose writes change servicing rows:
 * every row on the feed is a loan, a loan modification, a payment, a fee, a
 * facility or a deal in some state. A non-read action under one of them
 * (ADD_ / EDIT_ / SUBMIT_ / APPROVE_ / REJECT_ / DELETE_ …) is a write the
 * rows cannot be patched for — 18 row types, each its own shape — so the
 * store goes stale and the page reloads once (utils/generation.ts).
 */
export const SERVICING_SOURCE_NAMESPACES: readonly string[] = [
  'loans/',
  'loan_rollovers/',
  'loan_rollover_splits/',
  'loan_increases/',
  'loan_conversions/',
  'loan_merges/',
  'loan_rate_settings/',
  'principalPayments/',
  'payment-in-kind/',
  'payment-in-kinds/',
  'fee-payment/',
  'fee-payments/',
  'fees/',
  'facilities/',
  'deals/',
  'deal_amendment/',
  'facility_reverses/',
  'schedulerAmortization/',
]

const READ_ONLY_ACTION =
  /\/(GET_|SET_|START_LOADING|STOP_LOADING|LOADING|CLEAR_|RESET_)/

export const isServicingMutation = (type: unknown): boolean =>
  typeof type === 'string' &&
  SERVICING_SOURCE_NAMESPACES.some(ns => type.startsWith(ns)) &&
  !READ_ONLY_ACTION.test(type)

import { ServicingTypes } from 'app/models/servicing-params'

/**
 * The row types each source duck's contracts appear as on the feed. A loan
 * in Draft or Submitted is a New Borrowing row; an APPROVED loan is a
 * Maturing Loan, PIK Schedule or Add Rates row — and its menu offers
 * Delete, so a loan delete may hit any of them (see `servicingWriteOf`).
 */
const ROW_TYPES_BY_NAMESPACE: Record<string, readonly ServicingTypes[]> = {
  'loans/': [ServicingTypes.newBorrowing],
  'loan_rollovers/': [ServicingTypes.loanRollover],
  'loan_rollover_splits/': [ServicingTypes.loanRolloverSplit],
  'loan_increases/': [ServicingTypes.loanIncrease],
  'loan_conversions/': [ServicingTypes.loanConversion],
  'loan_merges/': [ServicingTypes.loanMerge],
  'loan_rate_settings/': [ServicingTypes.loanRateSetting],
  'principalPayments/': [ServicingTypes.principalPayment],
  'payment-in-kind/': [ServicingTypes.pik],
  'payment-in-kinds/': [ServicingTypes.pik],
  'fee-payment/': [ServicingTypes.feePayment],
  'fee-payments/': [ServicingTypes.feePayment],
  'facility_reverses/': [ServicingTypes.facilityReverse],
}

/** The row types that stay on the feed once approved (the rollover family reads its Approved template too). */
const LOAN_ROWS: readonly ServicingTypes[] = [
  ServicingTypes.newBorrowing,
  ServicingTypes.maturingLoan,
  ServicingTypes.pikScheduleLoan,
  ServicingTypes.loanAddRates,
]

const KEPT_WHEN_APPROVED: readonly ServicingTypes[] = [
  ServicingTypes.loanRollover,
]

export type ServicingWrite =
  | {
      verb: 'submit' | 'reject' | 'approve' | 'delete'
      types: readonly ServicingTypes[]
    }
  | { verb: 'other' }

/**
 * What a source duck's write means for the feed. Submit, reject, approve and
 * delete change one thing on one row, the same way for every row type, so
 * the store patches them from the ledger's answer and never refetches — a
 * read fired straight after the write could still see the old contract
 * (PQS projects asynchronously) and undo it on screen. Anything else (a
 * create, an edit, a facility or deal write) changes amounts, dates and
 * labels the statement computes: the store goes stale and reloads once.
 */
export const servicingWriteOf = (type: string): ServicingWrite => {
  const ns = Object.keys(ROW_TYPES_BY_NAMESPACE).find(n => type.startsWith(n))
  if (!ns) return { verb: 'other' }
  const name = type.slice(ns.length)
  const verb = name.startsWith('SUBMIT_')
    ? 'submit'
    : name.startsWith('REJECT_')
      ? 'reject'
      : name.startsWith('APPROVE_')
        ? 'approve'
        : name.startsWith('DELETE_')
          ? 'delete'
          : null
  if (!verb) return { verb: 'other' }
  const types =
    ns === 'loans/' && verb === 'delete'
      ? LOAN_ROWS
      : ROW_TYPES_BY_NAMESPACE[ns]
  return { verb, types }
}

export const staysWhenApproved = (type: ServicingTypes): boolean =>
  KEPT_WHEN_APPROVED.includes(type)
